import React, { useRef } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Counter from "@/components/Counter";
import { Section, SectionHeading, Eyebrow, Icon, PrimaryLink } from "@/components/common";
import { PILLARS, STATS, CLIENTS, INDUSTRIES, PARENT, BG, POSITIONING, BROCHURE } from "@/data/content";
import { PortfolioSection, ProcessGraphic } from "@/components/BrandVisuals";
import { useLang } from "@/context/LanguageContext";

export default function Home() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);

  return (
    <div>
      <Seo
        title="AI & Digital Transformation Partner in the Gulf"
        description={POSITIONING}
        path="/"
      />

      {/* HERO: white logo is directly on the continuous charcoal header/hero base. */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#262A30] text-white" data-testid="landing-hero">
        <Section className="relative grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-[1fr_1.05fr]">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300" data-testid="hero-parent"><span className="h-px w-8 bg-[#EF6672]" />A member of ERAM HOLDINGS</p>
            <h1 className="font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading">We engineer the<br /><span className="text-[#F08089]">intelligent future.</span></h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-200" data-testid="hero-positioning">{POSITIONING}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <PrimaryLink to="/contact" testid="hero-consult-cta">{t("cta.consult")}</PrimaryLink>
              <Link to="/services" className="group inline-flex items-center gap-2 px-2 py-3 text-sm font-semibold text-white" data-testid="hero-explore-cta">{t("cta.explore")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            </div>
            <p className="mt-10 border-t border-white/15 pt-5 text-xs tracking-wide text-slate-300" data-testid="hero-regions">Al Khobar · Dubai · Manama<span className="mx-3 text-slate-500">/</span>KSA & GCC</p>
          </motion.div>
          <motion.figure className="relative min-w-0" style={reduce ? undefined : { y: bgY }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
            <img src={BG.hero} alt="Connected industry, cloud infrastructure and operational intelligence — artwork from the ERAM DIGITAL brochure" width="1536" height="1024" fetchPriority="high" className="aspect-[6/5] w-full rounded-xl object-cover shadow-2xl" data-testid="hero-graphic" />
            <figcaption className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-300" data-testid="hero-graphic-caption"><span>FROM SENSOR TO BOARDROOM</span><span className="h-px flex-1 bg-white/20" /><span>IT + OT + AI</span></figcaption>
          </motion.figure>
        </Section>
      </section>
      <Section className="py-12">
        <div className="grid gap-8 md:grid-cols-3" data-testid="company-attributes">{BROCHURE.attributes.map((item) => <div key={item.title} className="flex items-start gap-4"><Icon name={item.icon} className="mt-1 h-7 w-7 shrink-0 text-brand-violet" /><div><h2 className="font-display text-lg font-bold">{item.title}</h2><p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p></div></div>)}</div>
      </Section>

      {/* CLIENT STRIP */}
      <div className="border-y border-[#EEF0F2] bg-[#F9FAFB] py-8">
        <p className="mb-6 text-center text-xs font-medium uppercase tracking-widest text-[#6B7280]">
          Technologies & platforms we build on
        </p>
        <Marquee gradient gradientColor="#FFFFFF" gradientWidth={80} speed={40} pauseOnHover>
          {CLIENTS.map((c) => (
            <span key={c} className="mx-10 font-display text-xl font-semibold text-[#6B7280] transition-colors hover:text-[#374151]">
              {c}
            </span>
          ))}
        </Marquee>
      </div>

      {/* VALUE PROP + PILLARS */}
      <Section className="py-28">
        <Reveal>
          <SectionHeading
            eyebrow="What we do"
            title="Four pillars,"
            gradient="one partner."
            subtitle="A technology partner from strategy and implementation to ongoing operations."
          />
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link
                to={`/services#${p.id}`}
                className="gradient-border group relative flex h-full flex-col overflow-hidden rounded-2xl p-8 transition-transform hover:-translate-y-1"
                data-testid={`home-pillar-${p.id}`}
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl gradient-bg text-white">
                  <Icon name={p.icon} className="h-7 w-7" />
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">{p.name}</h3>
                </div>
                <p className="text-sm font-medium text-brand-violet">{p.tagline}</p>
                <p className="mt-4 flex-1 text-base leading-relaxed text-[#4B5563]">{p.summary}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]">
                  Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* STATS BAND */}
      <div className="relative overflow-hidden border-y border-[#E5E7EB] bg-gradient-to-r from-brand-blue/10 via-brand-violet/10 to-brand-teal/10">
        <Section className="relative z-10 py-16">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1} className="text-center">
                <p className="font-display text-5xl font-bold tracking-tight text-[#0A0A0A] sm:text-6xl">
                  <Counter value={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-2 text-sm text-[#4B5563]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* PARENT GROUP */}
      <Section className="pt-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[#E5E7EB] glass p-10 sm:p-14">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-violet/15 blur-[120px]" />
            <div className="relative z-10 grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <Eyebrow>Our parent group</Eyebrow>
                <h2 className="font-display text-4xl font-semibold tracking-tight text-[#0A0A0A] sm:text-5xl">
                  Backed by <span className="gradient-text">ERAM HOLDINGS.</span>
                </h2>
                <p className="mt-5 max-w-xl leading-relaxed text-[#4B5563]">{PARENT.blurb}</p>
                <a
                  href={PARENT.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-7 inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] px-6 py-3 text-sm font-semibold text-[#0A0A0A] transition-colors hover:border-brand-violet hover:bg-[#F3F4F6]"
                  data-testid="home-parent-link"
                >
                  Visit ERAM HOLDINGS
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {PARENT.facts.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6] p-5">
                    <p className="text-sm font-medium leading-snug text-[#0A0A0A]">{f}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      <PortfolioSection />
      <ProcessGraphic />

      {/* INDUSTRIES teaser */}
      <Section className="pb-28">
        <Reveal>
          <SectionHeading eyebrow="Industries" title="Domain depth where it" gradient="counts." center />
        </Reveal>
        <div className="mx-auto mt-14 flex max-w-4xl flex-wrap justify-center gap-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 0.05}>
              <Link to="/industries" className="flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-[#F3F4F6] px-5 py-3 text-sm font-medium text-[#4B5563] transition-colors hover:border-brand-violet/50 hover:text-[#0A0A0A]" data-testid={`home-industry-${ind.name}`}>
                <Icon name={ind.icon} className="h-4 w-4 text-brand-violet" />
                {ind.name}
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[#E5E7EB] bg-gradient-to-br from-brand-blue/15 via-brand-violet/15 to-brand-teal/15 p-10 text-center sm:p-16 noise-overlay">
            <div className="relative z-10">
              <h2 className="font-display text-4xl font-semibold tracking-tight text-[#0A0A0A] sm:text-5xl">
                Ready to build what's <span className="gradient-text">next?</span>
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg text-[#4B5563]">
                Book a consultation and we'll scope the right pillar, engagement model and roadmap for your goals.
              </p>
              <ul className="mx-auto mt-8 flex max-w-2xl flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-[#4B5563]">
                {["No-obligation scoping call", "Regional delivery teams", "Vision 2030 alignment"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-teal" />{f}</li>
                ))}
              </ul>
              <div className="mt-10 flex justify-center">
                <PrimaryLink to="/contact" testid="home-final-cta">{t("cta.getStarted")}</PrimaryLink>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
